import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tokens } from '../styles/tokens';
import { shared } from '../styles/shared';
import type { DialogNode, ChoiceMessage } from '../types';

/**
 * Grille des boutons-images cliquables (2-4 options proposées par Sarah).
 *
 * Chaque `DialogNode` reçu a `type: 'choice'` et `message: ChoiceMessage`
 * — on cast localement pour profiter de l'auto-complétion sur `img`/`texte`
 * sans imposer une discriminated union plus stricte sur le type partagé.
 */
@customElement('sarah-choix')
export class SarahChoix extends LitElement {
  static styles = [
    tokens,
    shared,
    css`
      :host { display: block; }

      .choices {
        display: grid;
        grid-template-columns: 1fr;
        gap: 20px;
        margin-top: 30px;
        margin-bottom: 30px;
      }
      @media (min-width: 600px) {
        .choices {
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
      }
      @media (min-width: 720px) {
        .choices {
          margin-left: 62px;
          margin-top: 50px;
          margin-bottom: 50px;
        }
      }

      .choice {
        padding: 0 10px;
        display: flex;
      }
      .choice.choice-one { grid-column: 1 / -1; }

      .choice:focus { outline: none; }
      .choice:focus-visible .choice-content {
        border-color: var(--sarah-bleu);
        box-shadow: 0 0 0 3px rgba(53, 86, 137, .25);
      }

      .choice-content {
        position: relative;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        min-height: 88px;
        max-height: 184px;
        padding: 40px 5%;
        border: 2px solid #eae9e3;
        border-radius: var(--sarah-radius);
        text-align: center;
        cursor: pointer;
        transition: all .3s;
        background: #fff;
        box-shadow: var(--sarah-card-shadow);
      }
      .choice-content:hover { border-color: var(--sarah-border-hover); }

      .choice--is-selected .choice-content,
      .choice--is-selected .choice-content:hover {
        border-color: var(--sarah-bleu);
        background-color: #fff;
        box-shadow: 0 0 0 4px rgba(53, 86, 137, .39);
      }

      .choice--clicked .choice-content {
        border-color: var(--sarah-bleu);
        background-color: #eef3fa;
        box-shadow: 0 0 0 4px rgba(53, 86, 137, 0.39);
        transform: scale(0.97);
        transition: transform 0.15s, border-color 0.15s;
      }
      .choice--clicked .choice-content::after {
        content: "✓";
        position: absolute;
        top: 8px;
        right: 12px;
        color: var(--sarah-bleu);
        font-weight: 700;
        font-size: 20px;
        animation: checkPop 0.25s ease;
      }
      @keyframes checkPop {
        from { opacity: 0; transform: scale(0); }
        to   { opacity: 1; transform: scale(1); }
      }

      .choice-content img {
        width: auto;
        height: 64px;
        margin: 0 auto;
      }
      .choice-content p {
        font-family: var(--sarah-font-body);
        font-weight: 700;
        text-align: center;
        margin: 10px 0 0;
        width: 100%;
      }

      @media (max-width: 599px) {
        .choice-content { min-height: 60px; padding: 20px 5%; }
      }
    `,
  ];

  @property({ type: Array }) choices: DialogNode[] = [];

  @property({ type: String, attribute: 'saved-choice' }) savedChoice = '';

  @property({ type: String, attribute: 'assets-base' }) assetsBase = 'assets';

  @state() private clickedCle = '';

  private clickTimer: number | null = null;

  /** Délai du feedback visuel avant émission de l'événement `choice`. */
  private static readonly CLICK_FEEDBACK_MS = 250;

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.clickTimer !== null) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }
  }

  private onChoiceClick(cle: string): void {
    if (this.clickedCle) return;
    this.clickedCle = cle;
    this.clickTimer = window.setTimeout(() => {
      this.clickTimer = null;
      this.dispatchEvent(
        new CustomEvent('choice', {
          detail: { cle },
          bubbles: true,
          composed: true,
        }),
      );
    }, SarahChoix.CLICK_FEEDBACK_MS);
  }

  private onChoiceKeydown(event: KeyboardEvent, cle: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onChoiceClick(cle);
    }
  }

  render() {
    const onlyOne = this.choices.length < 2;
    return html`
      <div class="choices">
        ${this.choices.map((c) => {
          const msg = c.message as ChoiceMessage;
          const selected = c.cle === this.savedChoice;
          const clicked = c.cle === this.clickedCle;
          const classes = [
            'choice',
            selected ? 'choice--is-selected' : '',
            clicked ? 'choice--clicked' : '',
            onlyOne ? 'choice-one' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return html`
            <div
              class=${classes}
              role="button"
              tabindex="0"
              aria-label=${msg.texte}
              @click=${() => this.onChoiceClick(c.cle)}
              @keydown=${(e: KeyboardEvent) => this.onChoiceKeydown(e, c.cle)}
            >
              <div class="choice-content">
                ${msg.img
                  ? html`<img
                      src=${`${this.assetsBase}/${msg.img}`}
                      alt=${msg.texte}
                    />`
                  : nothing}
                <p class="choice-text">${msg.texte}</p>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sarah-choix': SarahChoix;
  }
}
